import { catalogSeedId, LEVEL_HINTS, type CatalogLevel, type CatalogSituation } from "./catalog";
import { FIRST_SCENE_SLUG } from "./firstScene";

export const TALKI_GUIDE = {
  name: "Talki",
  rolePt: "guia",
  src: "/characters/talki-guide.webp",
} as const;

export type PortraitId = "maya" | "diego";

export const PORTRAITS: Record<PortraitId, string> = {
  maya: "/characters/maya.webp",
  diego: "/characters/diego.webp",
};

export type ScenePartner = {
  name: string;
  rolePt: string;
  portrait: PortraitId;
  src: string;
  label: string;
};

export type SceneBrief = {
  place: string;
  /** Título curto do momento da cena, ex. "No balcão" — separado do lugar da missão. */
  sceneTitle: string;
  userRole: string;
  partner: ScenePartner;
  goal: string;
  complication: string;
  stuckAnchor: string;
  victory: string;
  guideOpen: string;
  /** Primeira fala do parceiro de cena, em inglês. */
  partnerOpen: string;
  hints: [string, string, string, string];
  living: boolean;
};

export const LIVING_SEED_IDS = [
  "checkin-aeroporto",
  "bagagem-perdida",
  "imigracao",
  "hotel-checkin",
  "alergia",
  "uber",
  "reuniao-standup",
  "call-cliente",
  "apresentar-ideia",
  "farmacia",
] as const;

export type LivingSeedId = (typeof LIVING_SEED_IDS)[number];

type LevelCopy = {
  complication: string;
  stuckAnchor: string;
  victory: string;
  modelPhrase: string;
};

type LivingSeed = {
  name: string;
  portrait: PortraitId;
  rolePt: string;
  place: string;
  sceneTitle: string;
  userRole: string;
  guideOpen: string;
  partnerOpen: string;
  keywords: string[];
  hintMissing: string;
  levels: Record<CatalogLevel, LevelCopy>;
};

const LIVING: Record<LivingSeedId, LivingSeed> = {
  "checkin-aeroporto": {
    name: "Maya",
    portrait: "maya",
    rolePt: "atendente do balcão",
    place: "no balcão do aeroporto",
    sceneTitle: "No balcão",
    userRole: "o passageiro",
    guideOpen: "Você está no balcão. Precisa embarcar hoje, mas pode ter um problema na reserva.",
    partnerOpen: "Good morning. May I see your passport, please?",
    keywords: ["passport", "booking", "gate"],
    hintMissing: "Falta dizer o que você precisa resolver agora no balcão.",
    levels: {
      beginner: {
        complication: "ela precisa do passaporte e do código da reserva para seguir",
        stuckAnchor: "o passaporte",
        victory: "confirmar o embarque",
        modelPhrase: "Hi, I'd like to check in. Here's my passport.",
      },
      intermediate: {
        complication: "a mala passou do peso e a fila está andando",
        stuckAnchor: "a mala extra",
        victory: "embarcar sem perder o voo",
        modelPhrase: "The bag is over the weight. What can I do?",
      },
      advanced: {
        complication: "o voo está cheio e o portão ficou em outro terminal",
        stuckAnchor: "overbooking",
        victory: "confirmar o embarque e saber para onde ir",
        modelPhrase: "The flight is full. I still need to get to my gate today.",
      },
    },
  },
  "bagagem-perdida": {
    name: "Maya",
    portrait: "maya",
    rolePt: "agente de bagagem",
    place: "no guichê de bagagem",
    sceneTitle: "No guichê",
    userRole: "o passageiro",
    guideOpen: "A esteira parou. Sua mala não chegou — e você precisa abrir um registro.",
    partnerOpen: "Hello. Which flight did you arrive on?",
    keywords: ["suitcase", "color", "address"],
    hintMissing: "Falta descrever a mala ou dizer onde você vai estar.",
    levels: {
      beginner: {
        complication: "ela precisa da cor, do nome e de um detalhe da mala",
        stuckAnchor: "a cor da mala",
        victory: "abrir o registro da mala",
        modelPhrase: "My suitcase didn't arrive. It's black, with a red ribbon.",
      },
      intermediate: {
        complication: "precisa de endereço e prazo de entrega",
        stuckAnchor: "o prazo",
        victory: "confirmar a entrega no hotel",
        modelPhrase: "Can you deliver it to my hotel tomorrow?",
      },
      advanced: {
        complication: "há itens caros e a indenização não é automática",
        stuckAnchor: "a indenização",
        victory: "negociar prazo e compensação",
        modelPhrase: "There are important items inside. What compensation can you offer?",
      },
    },
  },
  imigracao: {
    name: "Cole",
    portrait: "diego",
    rolePt: "oficial da imigração",
    place: "na fila da imigração",
    sceneTitle: "Na imigração",
    userRole: "o viajante",
    guideOpen: "Você está na imigração. O oficial quer entender o plano da viagem — sem um roteiro decorado.",
    partnerOpen: "Next, please. What's the purpose of your visit?",
    keywords: ["tourism", "days", "hotel"],
    hintMissing: "Falta dizer motivo, duração ou onde você se hospeda.",
    levels: {
      beginner: {
        complication: "ele pergunta de onde você vem e para onde vai",
        stuckAnchor: "o motivo",
        victory: "passar pelas perguntas básicas",
        modelPhrase: "I'm here for tourism. I'm staying for one week.",
      },
      intermediate: {
        complication: "ele pede motivo, duração e hospedagem",
        stuckAnchor: "a hospedagem",
        victory: "explicar o plano de turismo",
        modelPhrase: "I'm visiting for ten days. I'm staying at a hotel in the city center.",
      },
      advanced: {
        complication: "ele questiona se a história fecha",
        stuckAnchor: "o itinerário",
        victory: "explicar ida, estadia e volta com clareza",
        modelPhrase: "I have a return ticket on the 20th. Here's my hotel booking.",
      },
    },
  },
  "hotel-checkin": {
    name: "Sofia",
    portrait: "maya",
    rolePt: "recepcionista",
    place: "na recepção do hotel",
    sceneTitle: "Na recepção",
    userRole: "o hóspede",
    guideOpen: "Você acabou de chegar. A reserva pode não aparecer — e o hotel está cheio.",
    partnerOpen: "Welcome! Do you have a reservation with us?",
    keywords: ["reservation", "name", "nights"],
    hintMissing: "Falta confirmar nome, data ou pedir uma alternativa.",
    levels: {
      beginner: {
        complication: "ela precisa do nome e do número da reserva",
        stuckAnchor: "a reserva",
        victory: "pegar a chave",
        modelPhrase: "Hi, I have a reservation. The name is...",
      },
      intermediate: {
        complication: "o quarto não é o que você reservou",
        stuckAnchor: "o quarto",
        victory: "trocar o quarto sem pagar a mais",
        modelPhrase: "This isn't the room I booked. Can I change it?",
      },
      advanced: {
        complication: "seu nome não aparece e não há quarto padrão",
        stuckAnchor: "o overbooking",
        victory: "conseguir uma acomodação equivalente",
        modelPhrase: "The reservation isn't in the system. What other room do you have?",
      },
    },
  },
  alergia: {
    name: "Lena",
    portrait: "maya",
    rolePt: "garçom",
    place: "no restaurante",
    sceneTitle: "Na mesa",
    userRole: "o cliente",
    guideOpen: "O prato chegou. Tem algo que você não pode comer — e precisa pedir troca com educação.",
    partnerOpen: "Here you go — one special of the day. Enjoy!",
    keywords: ["allergic", "without", "instead"],
    hintMissing: "Falta dizer o ingrediente e pedir uma substituição.",
    levels: {
      beginner: {
        complication: "você precisa avisar que não pode amendoim",
        stuckAnchor: "a alergia",
        victory: "avisar a alergia a tempo",
        modelPhrase: "I'm allergic to peanuts. Can you make it without that?",
      },
      intermediate: {
        complication: "precisa confirmar cada ingrediente do prato",
        stuckAnchor: "os ingredientes",
        victory: "garantir um prato seguro",
        modelPhrase: "Does this dish have nuts or dairy?",
      },
      advanced: {
        complication: "o prato veio com o ingrediente que você não pode",
        stuckAnchor: "a substituição",
        victory: "explicar o problema e pedir outra opção",
        modelPhrase: "Sorry, this has nuts. Could I have something else, please?",
      },
    },
  },
  uber: {
    name: "Diego",
    portrait: "diego",
    rolePt: "motorista",
    place: "na calçada, esperando o carro",
    sceneTitle: "Na calçada",
    userRole: "o passageiro",
    guideOpen: "O carro está perto. Diego precisa confirmar o destino — e o ponto pode estar errado.",
    partnerOpen: "Hi there! Where are we headed today?",
    keywords: ["pickup", "destination", "around the corner"],
    hintMissing: "Falta uma referência do ponto ou confirmar o destino.",
    levels: {
      beginner: {
        complication: "ele quer confirmar o destino antes de seguir",
        stuckAnchor: "o destino",
        victory: "confirmar para onde ir",
        modelPhrase: "Hi, I'm going to the airport. Terminal 2.",
      },
      intermediate: {
        complication: "ele está no ponto errado e não te encontra",
        stuckAnchor: "o ponto",
        victory: "combinar um ponto visível",
        modelPhrase: "I'm at the main entrance, next to the coffee shop.",
      },
      advanced: {
        complication: "a rota no app está errada e precisa ser corrigida agora",
        stuckAnchor: "a rota",
        victory: "corrigir a corrida a tempo",
        modelPhrase: "That's the wrong address. Please go to 12 Oak Street.",
      },
    },
  },
  "reuniao-standup": {
    name: "Jordan",
    portrait: "diego",
    rolePt: "scrum master",
    place: "na stand-up da manhã",
    sceneTitle: "Na stand-up",
    userRole: "o colega",
    guideOpen: "A stand-up começou. Você precisa dizer o que fez e o que te bloqueia — em poucas frases.",
    partnerOpen: "Morning! Quick round — what did you work on yesterday?",
    keywords: ["yesterday", "today", "blocked"],
    hintMissing: "Falta o update, o bloqueio ou o próximo passo.",
    levels: {
      beginner: {
        complication: "Jordan pede o que você fez ontem, em 20 segundos",
        stuckAnchor: "o update",
        victory: "dar o update de ontem",
        modelPhrase: "Yesterday I finished the login page. Today I'll start the tests.",
      },
      intermediate: {
        complication: "a tarefa atrasou por dependência de outra pessoa",
        stuckAnchor: "o bloqueio",
        victory: "pedir ajuda e combinar o próximo passo",
        modelPhrase: "I'm blocked. I'm waiting on Ana for the API. Can someone help?",
      },
      advanced: {
        complication: "o prazo escapou e a sala espera um plano",
        stuckAnchor: "o prazo",
        victory: "assumir o atraso e propor um plano",
        modelPhrase: "We're behind. I can deliver a first version tomorrow if we cut the extra feature.",
      },
    },
  },
  "call-cliente": {
    name: "Alex",
    portrait: "diego",
    rolePt: "cliente",
    place: "numa call com o cliente",
    sceneTitle: "Na call",
    userRole: "o profissional",
    guideOpen: "O cliente está na linha. Ele não entendeu o prazo — e você precisa realinhar sem sumir.",
    partnerOpen: "Hi, thanks for joining. Can we go over the timeline?",
    keywords: ["deadline", "update", "next step"],
    hintMissing: "Falta reconhecer o problema ou combinar o acompanhamento.",
    levels: {
      beginner: {
        complication: "Alex quer confirmar o horário e o tema da call",
        stuckAnchor: "a pauta",
        victory: "abrir a call e confirmar o tema",
        modelPhrase: "Hi Alex. Today we'll review the timeline. Does that work?",
      },
      intermediate: {
        complication: "ele não entendeu o status da entrega",
        stuckAnchor: "o status",
        victory: "explicar o status sem jargão",
        modelPhrase: "The first version is ready. We're testing it this week.",
      },
      advanced: {
        complication: "ele está irritado porque o prazo estourou",
        stuckAnchor: "o prazo",
        victory: "acolher a reclamação e propor acompanhamento",
        modelPhrase: "You're right, we missed the date. Here's a new date and I'll update you tomorrow.",
      },
    },
  },
  "apresentar-ideia": {
    name: "Sam",
    portrait: "maya",
    rolePt: "líder da reunião",
    place: "numa reunião rápida",
    sceneTitle: "Na reunião",
    userRole: "o colega",
    guideOpen: "A reunião está andando. Alguém usou uma sigla — e você pode pedir para repetir sem travar.",
    partnerOpen: "Okay, quick one — any thoughts on this?",
    keywords: ["sorry", "mean", "repeat"],
    hintMissing: "Falta pedir para repetir ou responder com o que você sabe.",
    levels: {
      beginner: {
        complication: "Sam pede uma sugestão curta e você não ouviu a pergunta toda",
        stuckAnchor: "a pergunta",
        victory: "pedir repetição e dar uma ideia simples",
        modelPhrase: "Sorry, could you repeat that? I have one idea.",
      },
      intermediate: {
        complication: "alguém discorda e usa uma sigla que você não pegou",
        stuckAnchor: "a sigla",
        victory: "pedir explicação e defender a ideia",
        modelPhrase: "What does that acronym mean? My point is we should start smaller.",
      },
      advanced: {
        complication: "você tem dois minutos para vender risco, custo e ganho",
        stuckAnchor: "o pitch",
        victory: "fechar um próximo passo",
        modelPhrase: "Can we try a two-week test? Low cost, and we'll see the result.",
      },
    },
  },
  farmacia: {
    name: "Rita",
    portrait: "maya",
    rolePt: "farmacêutica",
    place: "na farmácia",
    sceneTitle: "No balcão",
    userRole: "o cliente",
    guideOpen: "Você está na farmácia. Precisa explicar um sintoma simples e confirmar como usar — sem virar consulta.",
    partnerOpen: "Hi, how can I help you today?",
    keywords: ["pain", "how often", "generic"],
    hintMissing: "Falta o sintoma ou confirmar como tomar.",
    levels: {
      beginner: {
        complication: "Rita pergunta o que você sente, em palavras simples",
        stuckAnchor: "o sintoma",
        victory: "comprar o item certo",
        modelPhrase: "I have a headache. Do you have something for pain?",
      },
      intermediate: {
        complication: "há genérico e original, e você quer a opção mais barata",
        stuckAnchor: "o genérico",
        victory: "escolher a opção e confirmar o uso",
        modelPhrase: "Is there a cheaper generic? How should I take it?",
      },
      advanced: {
        complication: "você já toma outro remédio e precisa checar com ela, sem diagnóstico",
        stuckAnchor: "a interação",
        victory: "confirmar o uso básico e os limites dela",
        modelPhrase: "I already take another medicine. Is this one okay, or should I ask a doctor?",
      },
    },
  },
};

const MALE_NAMES = ["Diego", "Cole", "Jordan", "Alex", "Omar", "Sam"] as const;
const FEMALE_NAMES = ["Maya", "Sofia", "Lena", "Rita", "Nina", "Amara"] as const;

const CATEGORY_USER_ROLE: Record<CatalogSituation["category"], string> = {
  viagem: "o viajante",
  comida: "o cliente",
  trabalho: "o profissional",
  saude: "o paciente",
  compras: "o cliente",
  social: "você",
  casa: "o morador",
  estudo: "o aluno",
  transporte: "o passageiro",
  emergencia: "quem precisa de ajuda",
  banco: "o cliente",
  entretenimento: "o visitante",
};

const CATEGORY_PLACE: Record<CatalogSituation["category"], string> = {
  viagem: "na viagem",
  comida: "no restaurante",
  trabalho: "no trabalho",
  saude: "no atendimento",
  compras: "na loja",
  social: "numa conversa",
  casa: "em casa",
  estudo: "na escola",
  transporte: "no caminho",
  emergencia: "num apuro",
  banco: "no banco",
  entretenimento: "na saída",
};

function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function rolePtFromAi(aiRole: string): string {
  const role = aiRole.toLowerCase();
  if (/check-in/.test(role)) return "atendente do balcão";
  if (/immigration/.test(role)) return "oficial da imigração";
  if (/baggage/.test(role)) return "agente de bagagem";
  if (/receptionist/.test(role)) return "recepcionista";
  if (/pharmacist/.test(role)) return "farmacêutico";
  if (/driver|rideshare/.test(role)) return "motorista";
  if (/scrum/.test(role)) return "scrum master";
  if (/waiter|server/.test(role)) return "garçom";
  if (/host/.test(role)) return "recepção";
  if (/barista/.test(role)) return "barista";
  if (/nurse|doctor|practitioner/.test(role)) return "profissional de saúde";
  if (/client/.test(role)) return "cliente";
  if (/manager/.test(role)) return "gerente";
  if (/officer/.test(role)) return "oficial";
  if (/agent|clerk|specialist|attendant/.test(role)) return "atendente";
  return "do outro lado";
}

function partnerFor(situation: CatalogSituation): ScenePartner {
  const seedId = catalogSeedId(situation);
  const living = LIVING[seedId as LivingSeedId];
  if (living) {
    return {
      name: living.name,
      rolePt: living.rolePt,
      portrait: living.portrait,
      src: PORTRAITS[living.portrait],
      label: `${living.name} — ${living.rolePt}`,
    };
  }

  const female = hashSeed(seedId) % 2 === 0;
  const names = female ? FEMALE_NAMES : MALE_NAMES;
  const name = names[hashSeed(seedId) % names.length];
  const portrait: PortraitId = female ? "maya" : "diego";
  const rolePt = rolePtFromAi(situation.aiRole);
  return {
    name,
    rolePt,
    portrait,
    src: PORTRAITS[portrait],
    label: `${name} — ${rolePt}`,
  };
}

function pickLevel<T>(levels: Record<CatalogLevel, T>, level: CatalogLevel): T {
  return levels[level] ?? levels.beginner;
}

function goalOf(situation: CatalogSituation): string {
  if (situation.slug === FIRST_SCENE_SLUG) return "sair com o cartão de embarque";
  const goal = situation.studentGoal.trim();
  if (!goal) return goal;
  return goal.charAt(0).toLowerCase() + goal.slice(1);
}

export function isLivingSeedId(value: string): value is LivingSeedId {
  return (LIVING_SEED_IDS as readonly string[]).includes(value);
}

export function scenePartner(situation: CatalogSituation): ScenePartner {
  return partnerFor(situation);
}

export function sceneBrief(situation: CatalogSituation): SceneBrief {
  const seedId = catalogSeedId(situation);
  const partner = partnerFor(situation);
  const userRole = LIVING[seedId as LivingSeedId]?.userRole ?? CATEGORY_USER_ROLE[situation.category];
  const goal = goalOf(situation);
  const living = LIVING[seedId as LivingSeedId];

  if (living) {
    const level = pickLevel(living.levels, situation.level);
    const keywords = living.keywords.join(", ");
    return {
      place: living.place,
      sceneTitle: living.sceneTitle,
      userRole: living.userRole,
      partner,
      goal,
      complication: level.complication,
      stuckAnchor: level.stuckAnchor,
      victory: level.victory,
      guideOpen: living.guideOpen,
      partnerOpen: living.partnerOpen,
      hints: [
        living.guideOpen,
        living.hintMissing,
        `Palavras que ajudam: ${keywords}.`,
        `Se quiser um empurrão: “${level.modelPhrase}”`,
      ],
      living: true,
    };
  }

  const place = CATEGORY_PLACE[situation.category];
  const complication = situation.subtitle.trim() || "pode aparecer um imprevisto";
  const guideOpen = `Você está ${place}. ${partner.name} é ${partner.rolePt}. Precisa ${goal}.`;
  return {
    place,
    sceneTitle: place.charAt(0).toUpperCase() + place.slice(1),
    userRole,
    partner,
    goal,
    complication,
    stuckAnchor: situation.subtitle || "o ponto difícil",
    victory: goal,
    guideOpen,
    partnerOpen: "Hi! How can I help you today?",
    hints: [
      guideOpen,
      `O que ainda não ficou claro: ${complication}.`,
      "Tente uma frase curta com o que você precisa.",
      "Pode repetir com calma. Uma frase inteligível já vale.",
    ],
    living: false,
  };
}

export function sceneHint(situation: CatalogSituation, level: number): string {
  const hints = sceneBrief(situation).hints;
  const index = Math.max(0, Math.min(hints.length - 1, level));
  return hints[index];
}

export function livingPromptNotes(situation: CatalogSituation): string {
  const brief = sceneBrief(situation);
  const health = situation.category === "saude"
    ? " Everyday communication only — no diagnosis, no prescriptions, no emergency instructions."
    : "";
  const notes = `${LEVEL_HINTS[situation.level]} You are ${brief.partner.name}. Complication: ${brief.complication}.${health} Accept short intelligible English that meets the goal. One complication, then a clear result.`;
  return notes.slice(0, 400);
}

export function voiceSceneContext(situation: CatalogSituation) {
  const brief = sceneBrief(situation);
  return {
    title: situation.title,
    subtitle: situation.subtitle,
    level: situation.level,
    aiRole: situation.aiRole,
    studentGoal: situation.studentGoal,
    promptNotes: livingPromptNotes(situation),
    characterName: brief.partner.name,
    characterRole: brief.partner.rolePt,
    complication: brief.complication,
    place: brief.place,
  };
}

export function isRepairTurn(text: string): boolean {
  return /didn'?t catch|didn'?t (quite )?(get|hear|understand)|say (that|it) again|come again|could you (say that|repeat)|take your time|what do you need today/i.test(
    text,
  );
}

export const UNCERTAIN_COACH =
  "Não identifiquei com segurança o ponto de prática. Quer repetir esta cena ou tentar outra relacionada?";
